import { computed } from 'vue';
import { PrintPluginStatus } from '@/modules/PrintService';
import { usePrintingStore, usePrinting } from '@/composition/use-printing';
import { usePrintingV2Store } from '@/stores/printing-v2.store';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { ILogicalPrinter } from '@/stores/printing-v2.config';
import {
  BridgeJobState,
  IBridgePrinter,
  IJobRecord,
} from '@/interfaces/print-bridge-protocol';
import {
  buildCodepageMegaTest,
  buildLongQrTestDoc,
  buildTestTicket,
  buildTestZplLabel,
  buildZplCalibrationDoc,
} from '@/lib/print/test-ticket';
import {
  IEscposPrinterSettings,
  IZplPrinterSettings,
} from '@/lib/print/printer-settings';
import { IPrinter } from '@/interfaces/IPrinter';

import { PRINTER_CODEPAGES } from '@/lib/print/codepages';

/** Codepages offered in the printer edit modal (canonical shared list). */
export const V2_CODEPAGES: readonly string[] = PRINTER_CODEPAGES;

export interface IPrinterStatusUi {
  labelKey: string;
  color: 'success' | 'error' | 'warning' | 'neutral';
}

export interface IJobStateUi {
  labelKey: string;
  color: 'success' | 'error' | 'warning' | 'info';
  icon: string;
  terminal: boolean;
}

export function transportIcon(transport: ILogicalPrinter['transport']): string {
  if (transport === 'tcp') return 'lan';
  if (transport === 'serial-bt') return 'bluetooth';
  return 'usb';
}

export function transportLabelKey(
  transport: ILogicalPrinter['transport']
): string {
  if (transport === 'tcp') return 'printingV2.transportNetwork';
  if (transport === 'serial-bt') return 'printingV2.transportBluetooth';
  return 'printingV2.transportUsb';
}

export function jobKindUi(
  kind: string | undefined
): {
  labelKey: string;
  icon: string;
} {
  switch (kind) {
    case 'comanda':
      return {
        labelKey: 'printingV2.kindComanda',
        icon: 'silverware-fork-knife',
      };
    case 'sticker':
      return { labelKey: 'printingV2.kindSticker', icon: 'tag-outline' };
    case 'test':
      return { labelKey: 'printingV2.kindTest', icon: 'printer-check' };
    default:
      return { labelKey: 'printingV2.kindTicket', icon: 'receipt' };
  }
}

export function jobStateUi(state: BridgeJobState): IJobStateUi {
  switch (state) {
    case 'done':
      return {
        labelKey: 'printingV2.jobPrinted',
        color: 'success',
        icon: 'check',
        terminal: true,
      };
    case 'failed':
      return {
        labelKey: 'printingV2.jobFailed',
        color: 'error',
        icon: 'close',
        terminal: true,
      };
    case 'unknown':
      return {
        labelKey: 'printingV2.jobVerify',
        color: 'warning',
        icon: 'help',
        terminal: true,
      };
    default:
      return {
        labelKey: 'printingV2.jobPrinting',
        color: 'info',
        icon: 'printer',
        terminal: false,
      };
  }
}

export function printerStatusUi(
  printer: IBridgePrinter | undefined
): IPrinterStatusUi {
  if (!printer) {
    return { labelKey: 'printingV2.statusOffline', color: 'neutral' };
  }
  if (printer.statusDetail?.includes('PAPER_OUT')) {
    return { labelKey: 'printingV2.statusPaperOut', color: 'warning' };
  }
  switch (printer.status) {
    case 'online':
      return { labelKey: 'printingV2.statusOnline', color: 'success' };
    case 'error':
      return { labelKey: 'printingV2.statusError', color: 'error' };
    case 'offline':
      return { labelKey: 'printingV2.statusOffline', color: 'neutral' };
    default:
      return { labelKey: 'printingV2.statusUnknown', color: 'neutral' };
  }
}

export function formatJobTime(epochMs: number): string {
  const date = new Date(epochMs);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  const time = `${String(date.getHours()).padStart(2, '0')}:${String(
    date.getMinutes()
  ).padStart(2, '0')}`;
  if (sameDay) return time;
  return `${String(date.getDate()).padStart(2, '0')}/${String(
    date.getMonth() + 1
  ).padStart(2, '0')} ${time}`;
}

/**
 * Shared derived state for the V2 printing modal tree. Call once per
 * component (composables are cheap; the heavy state lives in the stores).
 */
export function useV2PrintingUi() {
  const printingStore = usePrintingStore();
  const v2Store = usePrintingV2Store();
  const restaurantStore = useRestaurantStore();
  const printing = usePrinting();

  const bridge = computed(() => v2Store.getBridge());

  /** Bridge adopted AND its connect sequence (incl. hydration) finished. */
  const isConnected = computed(
    () =>
      printingStore.bridgeMode === 'bridge' &&
      bridge.value?.status.value === PrintPluginStatus.Connected
  );

  /** Editing allowed: connected, hydrated, and not schema-locked. */
  const canEdit = computed(
    () => isConnected.value && v2Store.hydrated && !v2Store.configReadOnly
  );

  const bridgePrinterByRef = computed(() => {
    const map = new Map<string, IBridgePrinter>();
    for (const printer of bridge.value?.bridgePrinters.value ?? []) {
      map.set(printer.ref, printer);
    }
    return map;
  });

  const logicalById = computed(() => {
    const map = new Map<number, ILogicalPrinter>();
    for (const logical of v2Store.displayConfig?.logicalPrinters ?? []) {
      map.set(logical.id, logical);
    }
    return map;
  });

  /** i18n-ready assignment descriptors for one logical printer. */
  function assignmentsOf(logicalId: number): { key: string; label: string }[] {
    const doc = v2Store.displayConfig;
    if (!doc) return [];
    const result: { key: string; label: string }[] = [];
    if (doc.assignments.ticket === logicalId) {
      result.push({ key: 'ticket', label: '' });
    }
    for (const stationKey of Object.keys(doc.assignments.comandaByStation)) {
      const stationId = Number(stationKey);
      if (doc.assignments.comandaByStation[stationId] === logicalId) {
        result.push({
          key: `comanda:${stationId}`,
          label: restaurantStore.stationName(stationId),
        });
      }
    }
    if (doc.assignments.sticker === logicalId) {
      result.push({ key: 'sticker', label: '' });
    }
    return result;
  }

  /** Printer display name for a job row: logical name > bridge name > ref. */
  function printerNameOf(printerRef: string): string {
    const logical = (v2Store.displayConfig?.logicalPrinters ?? []).find(
      (lp) => lp.ref === printerRef
    );
    if (logical) return logical.name;
    return bridgePrinterByRef.value.get(printerRef)?.name ?? printerRef;
  }

  function lastJobOf(printerRef: string): IJobRecord | null {
    const jobs = bridge.value?.jobs.value ?? [];
    return jobs.find((job) => job.printerRef === printerRef) ?? null;
  }

  /** IPrinter shim targeting a logical printer's bridge ref. */
  function printerTargetOf(
    logical: Pick<ILogicalPrinter, 'ref' | 'name'>
  ): IPrinter {
    return { id: logical.ref, name: logical.name, type: 'usb' };
  }

  /**
   * Sends the rendered test document (ESC/POS ticket or ZPL label) to a
   * logical printer through the strategy pipeline (kind 'test', no dedupe).
   * Uses the printer's SAVED per-printer settings (via printerSettingsOf's
   * projection + legacy fallbacks); `overrides` layer on top — the wizard
   * tests UNSAVED printers with its in-flight paper/codepage choices.
   * Throws on failure — callers surface the error.
   */
  async function printTestDocument(
    logical: Pick<ILogicalPrinter, 'ref' | 'name' | 'format'>,
    label: string,
    overrides?: {
      escpos?: Partial<IEscposPrinterSettings>;
      zpl?: Partial<IZplPrinterSettings>;
    }
  ): Promise<void> {
    const printer = printerTargetOf(logical);
    const resolved = printingStore.printerSettingsOf(printer);
    if (logical.format === 'zpl') {
      const zpl = { ...resolved.zpl, ...overrides?.zpl };
      // Offset triplet only when real labels would also carry it (explicit
      // saved settings). No caller passes zpl overrides today — the guard
      // keeps any future form-value test consistent with what a save would
      // produce. See buildTestZplLabel.
      const withOffsets = resolved.zplExplicit || !!overrides?.zpl;
      await printing.printZpl(printer, buildTestZplLabel(zpl, withOffsets), {
        kind: 'test',
        label,
      });
      return;
    }
    const escpos = { ...resolved.escpos, ...overrides?.escpos };
    await printing.printByteArray(printer, buildTestTicket(escpos), {
      kind: 'test',
      label,
    });
  }

  /**
   * "Probar códigos": one sample per canonical codepage in a single job so
   * the user can read which one renders Spanish correctly. Takes the FORM
   * values (possibly unsaved) so the user can iterate before saving.
   */
  async function printCodepageMegaTest(
    logical: Pick<ILogicalPrinter, 'ref' | 'name'>,
    escpos: Partial<IEscposPrinterSettings>,
    label: string
  ): Promise<void> {
    const bytes = buildCodepageMegaTest(PRINTER_CODEPAGES, escpos);
    await printing.printByteArray(printerTargetOf(logical), bytes, {
      kind: 'test',
      label,
    });
  }

  /** "Probar QR": AFIP-length QR rendered with the form's qrMode. */
  async function printQrTest(
    logical: Pick<ILogicalPrinter, 'ref' | 'name'>,
    escpos: Partial<IEscposPrinterSettings>,
    label: string
  ): Promise<void> {
    const bytes = await buildLongQrTestDoc(escpos);
    await printing.printByteArray(printerTargetOf(logical), bytes, {
      kind: 'test',
      label,
    });
  }

  /** "Imprimir calibración": ZPL border + rulers with the form's geometry. */
  async function printZplCalibration(
    logical: Pick<ILogicalPrinter, 'ref' | 'name'>,
    zpl: IZplPrinterSettings,
    label: string
  ): Promise<void> {
    await printing.printZpl(printerTargetOf(logical), buildZplCalibrationDoc(zpl), {
      kind: 'test',
      label,
    });
  }

  return {
    printingStore,
    v2Store,
    restaurantStore,
    printing,
    bridge,
    isConnected,
    canEdit,
    bridgePrinterByRef,
    logicalById,
    assignmentsOf,
    printerNameOf,
    lastJobOf,
    printTestDocument,
    printCodepageMegaTest,
    printQrTest,
    printZplCalibration,
  };
}
